import { Maybe } from '@/types/generated/graphql';
import { Button, Result } from 'antd';
import React, { ErrorInfo, ReactNode } from 'react';
import { config } from '@/lib/env-helpers';
import { capitalizeFirst } from '@shared/utils/capitalize';
import i18n from '@shared/utils/i18n';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Maybe<Error>;
  errorInfo: Maybe<ErrorInfo>;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    // Initialize the state
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error: null, errorInfo: null };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    // Log the error details
    console.error({ error, errorInfo });
    if (error instanceof Error) {
      this.setState({ hasError: true, error: error });
    }
  }

  render(): ReactNode {
    // If an error has occurred, show the fallback UI
    if (this.state.hasError) {
      return (
        <Result
          status="500"
          title={i18n.t('a_client_side_error_has_occurred_try_again_later')}
          subTitle={
            config?.IS_DEVELOP && (
              <div className="text-red-500 italic max-w-[520px] mx-auto">
                {this?.state?.error?.stack?.split('\n').slice(0, 2).join('\n')}
                {this?.state?.errorInfo?.componentStack?.split('\n').slice(0, 2).join('\n')}
              </div>
            )
          }
          extra={<Button onClick={() => window.location.reload()}>{capitalizeFirst(i18n.t('try_again'))}</Button>}
        />
      );
    }

    // Render children if no error
    return this.props.children;
  }
}
